import { useEffect, useRef, useState } from "react";
import ProcessingLoader from "./components/ProcessingLoader";

const MicroFrontend = ({ name, host, history, options }) => {
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(false);

  const containerId = `${name}-container`;
  const scriptId = `mf-script-${name}`;
  const cssId = `mf-css-${name}`;

  const getMount = () =>
    window[`render${name}`] || window[`createRoot${name}`];

  const getUnmount = () => window[`unmount${name}`];

  const cleanupAssets = () => {
    document.getElementById(scriptId)?.remove();
    document.getElementById(cssId)?.remove();

    delete window[`render${name}`];
    delete window[`createRoot${name}`];
    delete window[`unmount${name}`];
  };

  const safeUnmount = () => {
    const unmount = getUnmount();
    const container = document.getElementById(containerId);

    if (unmount && container) {
      try {
        unmount(containerId);
      } catch (e) {
        console.warn("Unmount failed:", name, e);
      }
    }
  };

  const render = () => {
    if (mountedRef.current) return;

    const mount = getMount();
    if (!mount) return;

    try {
      mount(containerId, history, options);
      mountedRef.current = true;
      setLoading(false);
    } catch (e) {
      console.error("Mount error:", name, e);
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      mountedRef.current = false;

      cleanupAssets();

      // Locally each remote runs as its own dedicated dev server (its own
      // port), so its manifest lives at that host's own root. In every
      // deployed env these remotes share one origin, disambiguated by the
      // /${name}/ path prefix.
      const isLocalHost = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(
        host
      );
      const manifestUrl = isLocalHost
        ? `${host}/asset-manifest.json`
        : `${host}/${name}/asset-manifest.json`;

      try {
        const res = await fetch(manifestUrl, {
          cache: "no-cache",
        });

        const contentType = res.headers.get("content-type") || "";

        if (!res.ok) {
          throw new Error(`Manifest fetch failed: ${res.status}`);
        }

        // ❌ IMPORTANT FIX: HTML instead of JSON detection
        if (!contentType.includes("application/json")) {
          const text = await res.text();
          console.error("Expected JSON but got HTML:", text.slice(0, 200));
          throw new Error("Manifest is not JSON (likely wrong path or SPA fallback)");
        }

        const manifest = await res.json();

        let jsFile;
        let cssFile;
        let isVite = false;

        if (manifest?.files) {
          // CRA
          jsFile = manifest.files["main.js"];
          cssFile = manifest.files["main.css"];
        } else {
          // Vite / custom
          isVite = true;
          const entry = Object.values(manifest || {}).find((f) => f?.isEntry);

          if (!entry) throw new Error("No entry found in manifest");

          jsFile = entry.file;
          cssFile = entry.css?.[0];
        }

        if (!jsFile) throw new Error("JS entry missing in manifest");

        const toUrl = (base, file) =>
          file.startsWith("/") ? `${base}${file}` : `${base}/${file}`;

        const script = document.createElement("script");
        script.id = scriptId;
        script.src = toUrl(host, jsFile);
        script.crossOrigin = "anonymous";

        if (isVite) script.type = "module";

        script.onload = () => {
          if (!cancelled) render();
        };

        script.onerror = () => {
          console.error("Script load failed:", name);
          setLoading(false);
        };

        document.head.appendChild(script);

        if (cssFile) {
          const link = document.createElement("link");
          link.id = cssId;
          link.rel = "stylesheet";
          link.href = toUrl(host, cssFile);
          link.crossOrigin = "anonymous";
          document.head.appendChild(link);
        }
      } catch (err) {
        console.error("Microfrontend load error:", name, err);

        const container = document.getElementById(containerId);
        if (container) {
          container.innerHTML =
            "Microfrontend failed to load. Please try again later.";
          container.className = "alert alert-danger";
        }

        setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;

      safeUnmount();
      cleanupAssets();

      mountedRef.current = false;

      window.dispatchEvent(
        new CustomEvent("LOADER", { detail: { loading: false } })
      );
    };
  }, [name, host]);

  return (
    <>
      {loading && <ProcessingLoader />}
      <div id={containerId} />
    </>
  );
};

export default MicroFrontend;